import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useStyles } from './ModelsPage.styles.js';

import moment from 'moment';
import _ from 'lodash';
import Grid from '@material-ui/core/Grid';
import ReactTimeAgo from 'react-time-ago';

import InfiniteScroll from 'react-infinite-scroll-component';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import AddCircleIcon from '@mui/icons-material/AddCircle';

import coverImg from '../../img/cover.png';
import editImg from '../../img/image-edit-fill.svg';
import infoIcon from '../../img/info.svg';

import CommunityUploadModal from '../layout/CommunityUploadModal';
import ConfirmationModal from '../layout/ConfirmationModal.js';
import EditLabelModal from '../layout/EditLabelModal.js';
import EditCommunityModal from '../layout/EditCommunityModal';
import TrainingInfoModal from '../layout/TrainingInfoModal.js';
import { showMessageV2 } from '../../utils/page.utils.js';

import {
  checkTrainingCompletion,
  checkFailedTrainingsAndReturnCredit,
  updateTrainingById,
  updateCommunityItemById,
  deleteCommunityItemById,
  fetchTrainingsChunk,
  fetchPagedUserCommunityItems,
  getCommunitySampleSignedUrl,
  likeCommunityItem,
  dislikeCommunityItem,
  updateTTSTrainingById,
  fetchTTSTrainingsChunk,
  fetchTTITrainingsChunk,
  checkTTITrainingCompletion,
  updateTTITrainingsById,
  updateTTITrainingsThumbnailById,
  getRecentTrainingService,
} from '../../services/page.services.js';

import getHistoryTabs from './historyTabs.js';

function ModelsPage() {
  const { t } = useTranslation();
  const user = useSelector(state => state.user);
  const userId = user?.id;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [alignment, setAlignment] = useState('music');
  const historyTabs = getHistoryTabs(t, 'models');

  // initData
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  // tti training thumbnail file
  const fileInputRef = useRef(null);

  // query
  const [query, setQuery] = useState('');

  //play
  const [selectedAudio, setSelectedAudio] = React.useState();
  const [playing, setPlaying] = useState(false);
  const [selectedCommunityId, setSelectedCommunityId] = useState('');

  // pagination
  const chunk = 50;
  const [chunkPage, setChunkPage] = useState(0);
  const [length, setLength] = useState(0);
  const controllerRef = useRef(null);
  const [paginationInfo, setPaginationInfo] = useState({});

  // modal
  const [editModalProps, setEditModalProps] = useState({});
  const [editCommunityModalProps, setEditCommunityModalProps] = useState({});
  const [confirmationModalProps, setConfirmationModalProps] = useState({});
  const [communityUploadProps, setCommunityUploadProps] = useState({});
  const [trainingInfoModalProps, setTrainingInfoModalProps] = useState({});

  const getRecentService = async () => {
    const service = await getRecentTrainingService();
    setAlignment(service);
  };

  useEffect( () => {
    if (userId) {
      getRecentService();
    }
  }, [userId]);

  //add event listened to be triggered whenever the played song ends
  useEffect(() => {
    if (selectedAudio) {
      selectedAudio.addEventListener('ended', () => {
        setSelectedCommunityId('');
        setPlaying(false);
      });
    }
    return () => {
      if (selectedAudio) {
        selectedAudio.pause();
        selectedAudio.removeEventListener('ended', () => {
          setSelectedCommunityId('');
          setPlaying(false);
        });
      }
    };
  }, [selectedAudio]);

  //when people click play button
  useEffect(() => {
    if (selectedAudio) {
      playing ? selectedAudio.play() : selectedAudio.pause();
    }
  }, [playing]);

  useEffect(() => {
    const debounced = _.debounce(() => initData(false), 300);

    if (userId) {
      debounced();
    }

    return () => {
      debounced.cancel();
    };
  }, [userId, alignment, query]);

  const initData = async scroll => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    controllerRef.current = new AbortController();
    const signal = controllerRef.current.signal;

    try {
      if (alignment === 'music') {
        let trainingsChunk;
        await checkFailedTrainingsAndReturnCredit();

        trainingsChunk = await fetchTrainingsChunk(chunk, scroll ? chunkPage : 0, query, signal);
        let trainings = trainingsChunk.rows;

        const incompleteList = _.filter(trainings, { status: 'PENDING' });
        if (_.size(incompleteList) > 0) {
          await checkTrainingCompletion(userId, incompleteList);
          trainingsChunk = await fetchTrainingsChunk(chunk, scroll ? chunkPage : 0, query, signal);
          trainings = trainingsChunk.rows;
        }

        const cleanedData = trainings.map(
          ({
            id,
            label,
            status,
            createdAt,
            tier,
            shared,
            purchaseId,
            external,
            fileList,
            needMdx,
            errorFile,
            pipelineStatus,
          }) => {
            const parsedFileList = fileList ? JSON.parse(fileList) : null;
            return {
              id,
              label,
              createdAt: moment(createdAt).format('MM/DD/YY hh:mm A'),
              status,
              shared,
              tag: tier === 'FREE' && !purchaseId ? `[${t('historyTab.freeVersionTag')}]` : '',
              fileList: parsedFileList,
              needMdx,
              external,
              errorFile,
              pipelineStatus,
            };
          }
        );

        if (scroll) {
          setData(prevData => [...prevData, ...cleanedData]);
          setChunkPage(prevChunkPage => prevChunkPage + 1);
        } else {
          setData(cleanedData);
          setChunkPage(1);
        }
        setLength(trainingsChunk.count);
      } else if (alignment === 'speech') {
        let trainingsChunk;
        if (scroll) {
          trainingsChunk = await fetchTTSTrainingsChunk(chunk, chunkPage, query, signal);
        } else {
          trainingsChunk = await fetchTTSTrainingsChunk(chunk, 0, query, signal);
        }
        let trainings = trainingsChunk.rows;

        const cleanedData = trainings.map(({ id, label, status, createdAt, tier, shared, purchaseId }) => {
          return {
            id,
            label,
            createdAt: moment(createdAt).format('MM/DD/YY hh:mm A'),
            status,
            shared,
            tag: '',
          };
        });

        if (scroll) {
          setData([...data, ...cleanedData]);
          setChunkPage(prevChunkPage => prevChunkPage + 1);
        } else {
          setData(cleanedData);
          setChunkPage(1);
        }
        setLength(trainingsChunk.count);
      } else if (alignment === 'image') {
        let trainingsChunk;
        trainingsChunk = await fetchTTITrainingsChunk(chunk, scroll ? chunkPage : 0, query, signal);
        let trainings = trainingsChunk.rows;

        const incompleteList = _.filter(trainings, {
          status: 'PENDING',
        });

        if (_.size(incompleteList) > 0) {
          await checkTTITrainingCompletion(userId, incompleteList);
          trainingsChunk = await fetchTTITrainingsChunk(chunk, scroll ? chunkPage : 0, query, signal);
          trainings = trainingsChunk.rows;
        }

        const cleanedData = trainings.map(({ id, label, status, createdAt, outputFormat, usage, thumbnail }) => {
          return {
            id,
            label,
            createdAt: moment(createdAt).format('MM/DD/YY hh:mm A'),
            status,
            outputFormat,
            usage,
            thumbnail,
          };
        });

        if (scroll) {
          setData(prevData => [...prevData, ...cleanedData]);
          setChunkPage(prevChunkPage => prevChunkPage + 1);
        } else {
          setData(cleanedData);
          setChunkPage(1);
        }
        setLength(trainingsChunk.count);
      } else {
        const communityItems = await fetchPagedUserCommunityItems(1, chunk, query, null, 'recent', userId);
        setData(communityItems.rows);
        setFilteredData(communityItems.rows);
        setPaginationInfo(communityItems.pagination);
      }
    } catch (err) {
      console.log(err);
      if (err.name != 'CanceledError') {
        showMessageV2(dispatch, t('historyTab.modal.error'), { reloadOnClose: true });
      }
    }
  };

  const fetchMoreData = async () => {
    try {
      const communityItems = await fetchPagedUserCommunityItems(
        paginationInfo.next,
        chunk,
        query,
        null,
        'recent',
        userId
      );
      setData(prevData => [...prevData, ...communityItems.rows]);
      setFilteredData(prevData => [...prevData, ...communityItems.rows]);
      setPaginationInfo(communityItems.pagination);
    } catch (err) {
      if (err.name != 'CanceledError') {
        showMessageV2(dispatch, t('historyTab.modal.error'), { reloadOnClose: true });
      }
    }
  };

  const getBlobFromS3Url = async url => {
    const res = await fetch(url);
    return res.blob();
  };

  const handleShare = async id => {
    setCommunityUploadProps({
      open: true,
      selectedModelId: id,
      onCommunityModalClose: () => {
        setCommunityUploadProps({ open: false });
      },
    });
  };

  const handleCommunityEdit = async (id, label, anonymous) => {
    setEditCommunityModalProps({
      open: true,
      label,
      anonymous,
      onEdit: async (changedLabel, changedAnonymous, changedImage) => {
        setEditCommunityModalProps({ open: true, loading: true });
        const updatedCommunityItem = await updateCommunityItemById(
          id,
          { label: changedLabel, anonymous: changedAnonymous },
          changedImage
        );

        const copiedFilteredData = filteredData.map(obj => {
          if (obj.id === updatedCommunityItem.id) {
            let newObj = { ...obj };
            newObj.image = updatedCommunityItem.image;
            newObj.label = updatedCommunityItem.label;
            newObj.username = updatedCommunityItem.username;
            newObj.anonymous = updatedCommunityItem.anonymous;
            return newObj;
          }
          return obj;
        });

        const copiedData = data.map(obj => {
          if (obj.id === id) {
            let newObj = { ...obj };
            newObj.image = updatedCommunityItem.image;
            newObj.label = updatedCommunityItem.label;
            newObj.username = updatedCommunityItem.username;
            newObj.anonymous = updatedCommunityItem.anonymous;
            return newObj;
          }
          return obj;
        });
        setData(copiedData);
        setFilteredData(copiedFilteredData);
        setEditCommunityModalProps({ open: false });
      },
      onEditCommunityModalClose: () => {
        setEditCommunityModalProps({ open: false });
      },
    });
  };

  const handleDelete = async (id, type, alignment) => {
    setConfirmationModalProps({
      open: true,
      message:
        alignment === 'music' && type !== 'FAILED'
          ? t('historyTab.confirmModelDelete')
          : type !== 'FAILED' && t('historyTab.confirmDelete'),
      onConfirmation: async response => {
        if (response === true) {
          setConfirmationModalProps({ open: true, loading: true });
          if (alignment === 'music') {
            await updateTrainingById(id, { deletedAt: moment() });
          } else if (alignment === 'speech') {
            await updateTTSTrainingById(id, { deletedAt: moment() });
          } else if (alignment === 'image') {
            await updateTTITrainingsById(id, { deletedAt: moment() });
          } else {
            await deleteCommunityItemById(id, { deletedAt: moment() });
          }

          if (alignment !== 'community') {
            let copiedData = [...data];
            copiedData = copiedData.filter(obj => obj.id !== id);
            setData(copiedData);
          } else {
            const copiedCommunityItems = data.filter(item => {
              return item.id !== id;
            });
            setData(copiedCommunityItems);
            setFilteredData(copiedCommunityItems);
          }
        }
        setConfirmationModalProps({ open: false });
      },
      onClose: () => setConfirmationModalProps({ open: false }),
    });
  };

  const handleEdit = async (id, label, alignment) => {
    setEditModalProps({
      open: true,
      label,
      alignment,
      onEdit: async changedLabel => {
        setEditModalProps({ open: true, loading: true });
        if (alignment === 'music') {
          await updateTrainingById(id, { label: changedLabel });
        } else if (alignment === 'speech') {
          await updateTTSTrainingById(id, { label: changedLabel });
        } else if (alignment === 'image') {
          await updateTTITrainingsById(id, { label: changedLabel });
        } else  {
          await updateCommunityItemById(id, { label: changedLabel });
        }
        let copiedData = data.map(obj => {
          if (obj.id === id) {
            return { ...obj, label: changedLabel };
          }
          return obj;
        });
        setData(copiedData);

        if (alignment === 'community') {
          setData(copiedData);
          let copiedFilteredData = filteredData.map(obj => {
            if (obj.id === id) {
              return { ...obj, label: changedLabel };
            }
            return obj;
          });
          setData(copiedData);
          setFilteredData(copiedFilteredData);
        }
        setEditModalProps({ open: false });
      },
      onClose: () => {
        setEditModalProps({ open: false });
      },
    });
  };

  const onThumbnailChange = id => async e => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const updated = await updateTTITrainingsThumbnailById(id, file);
      setData(prev => prev.map(item => (item.id === updated.id ? { ...item, thumbnail: updated.thumbnail } : item)));
    } catch (err) {
      showMessageV2(dispatch, t('ttiInferenceTab.submit.tryAgain'));
    }
  };

  const handlePlay = async (communityItemId) => {
    if (selectedCommunityId === communityItemId) {
      setPlaying(true);
    } else {
      setPlaying(false);
      const sampleVoiceUrl = await getCommunitySampleSignedUrl(communityItemId);
      const sampleVoiceBlob = await getBlobFromS3Url(sampleVoiceUrl);
      if (sampleVoiceBlob.size > 2000) {
        const audio = new Audio(URL.createObjectURL(sampleVoiceBlob.slice(0, sampleVoiceBlob.size, `audio/mp3`)));
        setSelectedAudio(audio);
        setPlaying(true);
        setSelectedCommunityId(communityItemId);
      }
    }
  };

  const handlePlay2 = _.debounce(handlePlay, 500, { leading: true });

  const handlePause = async () => {
    setPlaying(false);
  };

  const handleLike = async communityItemId => {
    likeCommunityItem(communityItemId);
    const copiedData = data.map(obj => {
      if (obj.id === communityItemId) {
        return {
          ...obj,
          likedNumber: Number(obj.likedNumber) + 1,
          liked: true,
        };
      }
      return obj;
    });
    const copiedFilteredData = filteredData.map(obj => {
      if (obj.id === communityItemId) {
        return {
          ...obj,
          likedNumber: Number(obj.likedNumber) + 1,
          liked: true,
        };
      }
      return obj;
    });

    setData(copiedData);
    setFilteredData(copiedFilteredData);
  };

  const handleDislike = async communityItemId => {
    dislikeCommunityItem(communityItemId);
    const copiedData = data.map(obj => {
      if (obj.id === communityItemId) {
        let newObj = { ...obj };
        newObj.likedNumber = Number(obj.likedNumber) - 1;
        newObj.liked = false;
        return newObj;
      }
      return obj;
    });
    const copiedFilteredData = filteredData.map(obj => {
      if (obj.id === communityItemId) {
        let newObj = { ...obj };
        newObj.likedNumber = Number(obj.likedNumber) - 1;
        newObj.liked = false;
        return newObj;
      }
      return obj;
    });

    setData(copiedData);
    setFilteredData(copiedFilteredData);
  };

  const handleTrainingInfo = async (label, external, fileList, needMdx, shared, errorFile, pipelineStatus, status) => {
    setTrainingInfoModalProps({
      open: true,
      label,
      external,
      fileList,
      needMdx,
      shared,
      errorFile,
      pipelineStatus,
      status,
      onTrainingInfoModalClose: () => {
        setTrainingInfoModalProps({ open: false });
      },
    });
  };

  const classes = useStyles();
  return (
    <>
      <CommunityUploadModal {...communityUploadProps} />
      <ConfirmationModal {...confirmationModalProps} />
      <EditLabelModal {...editModalProps} />
      <EditCommunityModal {...editCommunityModalProps} />
      <TrainingInfoModal {...trainingInfoModalProps} />
      <div className={classes.container}>
        <div className={classes.pageTitle}>{t('historyTab.models.title')}</div>
        <div className={classes.selectContainer}>
          <Select
            value={alignment}
            onChange={e => {
              setAlignment(e.target.value);
              setData([]);
              setQuery('');
              setChunkPage(0);
            }}
            size='small'
            autoWidth
            className={classes.select}
            MenuProps={{
              disableScrollLock: true,
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              slotProps: {
                paper: { className: classes.selectPaper },
              },
            }}
          >
            {historyTabs.map(({ title, tab }) => (
              <MenuItem value={tab} key={tab} dense>
                {title}
              </MenuItem>
            ))}
          </Select>
          <input
            className={classes.searchbar}
            placeholder={t('historyTab.searchbar')}
            value={query}
            onChange={query => setQuery(query.target.value)}
          />
        </div>
        {alignment === 'music' && (
          <InfiniteScroll
            className={classes.infiniteScroll}
            dataLength={data.length}
            next={() => initData(true)}
            hasMore={data.length < length}
            loader={<div className={classes.pagination}>{t('historyTab.pagination.loader')}</div>}
          >
            {data
              .sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)))
              .map(
                ({
                  id,
                  label,
                  status,
                  createdAt,
                  shared,
                  external,
                  fileList,
                  needMdx,
                  errorFile,
                  pipelineStatus,
                }) => {
                  return (
                    <div className={classes.card} key={id}>
                      <div className={classes.cardLabel}>
                        {label}
                        <EditIcon
                          className={classes.cardButton}
                          onClick={event => {
                            event.stopPropagation();
                            handleEdit(id, label, alignment);
                          }}
                        />
                      </div>
                      <div className={classes.cardActions}>
                        <div className={classes.cardTimestampContainer}>
                          <div className={classes.cardTimestamp}>{createdAt}</div>
                          {status === 'FAILED' && <div className={classes.cardStatus}>{t('historyTab.failed')}</div>}
                          {status === 'PENDING' && (
                            <div className={classes.cardStatus}>
                              {pipelineStatus
                                ? t(`pipeline.inferenceStatusMessage.${pipelineStatus}`)
                                : t('historyTab.inProgress')}
                            </div>
                          )}
                        </div>
                        <div className={classes.cardButtonGrid}>

                          {status === 'COMPLETE' && !shared && (
                            <div className={classes.cardButton}>
                              <ShareOutlinedIcon
                                disabled={shared}
                                onClick={() => {
                                  handleShare(id);
                                }}
                              />
                            </div>
                          )}
                          <div className={classes.cardButton}>
                            <img
                              src={infoIcon}
                              alt="tooltip-img"
                              onClick={() =>
                                handleTrainingInfo(
                                  label,
                                  external,
                                  fileList,
                                  needMdx,
                                  shared,
                                  errorFile,
                                  pipelineStatus,
                                  status
                                )
                              }
                            />
                          </div>
                          {(status === 'COMPLETE' || status === 'FAILED') && (
                            <div
                              className={classes.cardButton}
                              onClick={event => {
                                event.stopPropagation();
                                handleDelete(id, status, alignment);
                              }}
                            >
                              <DeleteIcon />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }
              )}
          </InfiniteScroll>
        )}
        {alignment === 'speech' && (
          <InfiniteScroll
            className={classes.infiniteScroll}
            dataLength={data.length}
            next={() => initData(true)}
            hasMore={data.length < length}
            loader={<div className={classes.pagination}>{t('historyTab.pagination.loader')}</div>}
          >
            {data
              .sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)))
              .map(
                ({ id, label, createdAt, status, tag }) => {
                  return (
                    <div className={classes.card} key={id}>
                      <div className={classes.cardLabel}>
                        {label} {tag}
                        <EditIcon
                          onClick={event => {
                            event.stopPropagation();
                            handleEdit(id, label, alignment);
                          }}
                          className={classes.cardButton}
                        />
                      </div>
                      <div className={classes.cardActions}>
                        <div className={classes.cardTimestampContainer}>
                          <div className={classes.cardTimestamp}>{createdAt}</div>
                          {status === 'FAILED' && <div className={classes.cardStatus}>{t('historyTab.failed')}</div>}
                          {status === 'PENDING' && <div className={classes.cardStatus}>{t('historyTab.inProgress')}</div>}
                        </div>
                        <div className={classes.cardButtonGrid}>
                          {(status === 'COMPLETE' || status === 'FAILED') && (
                            <div className={classes.cardButton}>
                              <DeleteIcon
                                onClick={event => {
                                  event.stopPropagation();
                                  handleDelete(id, status, alignment);
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }
              )}
          </InfiniteScroll>
        )}
        {alignment === 'community' && (
          <InfiniteScroll
            className={classes.infiniteScroll}
            dataLength={data.length}
            next={fetchMoreData} 
            hasMore={data.length < length}
            loader={<div className={classes.pagination}>{t('historyTab.pagination.loader')}</div>}
          >
            {data
              .sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)))
              .map(
                ({
                  id,
                  label,
                  username,
                  createdAt,
                  status,
                  image,
                  likedNumber,
                  numberUsed,
                  anonymous,
                  liked,
                }) => {
                  return (
                    <div className={classes.communityCard} key={id}>
                      <div className={classes.communityCardColumn}>
                        <img className={classes.coverImg} src={image}/>
                        <div className={classes.communityCardInfo}>
                          <div className={classes.communityCardLabel}>{label}</div>
                          <div className={classes.communityCardSubInfo}>
                            <div>{`${numberUsed} creations`}</div>
                            <ReactTimeAgo date={createdAt} locale="en-US" />
                          </div>
                        </div>
                      </div>
                      <div className={classes.communityCardActions}>
                        <div className={classes.cardButtonGrid}>
                          {playing && id === selectedCommunityId ? (
                            <div className={classes.communityCardButton}>
                              <PauseCircleOutlineIcon
                                onClick={event => {
                                  event.stopPropagation();
                                  handlePause(id, label);
                                }}
                              />
                            </div>
                          ) : (
                            <div className={classes.communityCardButton}>
                              <PlayCircleOutlineIcon
                                onClick={event => {
                                  event.stopPropagation();
                                  handlePlay2(id, label);
                                }}
                              />
                            </div>
                          )}
                          <div className={classes.communityCardButton}>
                            <EditIcon onClick={() => handleCommunityEdit(id, label, anonymous)} />
                          </div>
                          <div className={classes.communityCardButton}>
                            <DeleteIcon onClick={() => handleDelete(id, status, alignment)} />
                          </div>
                        </div>
                        <div className={classes.communityCardLikes}>
                          {!liked && (
                            <FavoriteBorderRoundedIcon
                              className={classes.favoriteImg}
                              onClick={() => handleLike(id)}
                              fontSize="inherit"
                            />
                          )}
                          {liked && (
                            <FavoriteIcon
                              className={classes.favoriteFullImg}
                              onClick={() => handleDislike(id)}
                              fontSize="inherit"
                            />
                          )}
                          <div className={classes.numberLikes}>{likedNumber}</div>
                        </div>
                      </div>
                    </div>
                  );
                }
              )}
          </InfiniteScroll>
        )}
        {alignment === 'image' && (
          <InfiniteScroll
            className={classes.infiniteScroll}
            dataLength={data.length}
            next={() => initData(true)}
            hasMore={data.length < length}
            loader={<div className={classes.pagination}>{t('historyTab.pagination.loader')}</div>}
          >
            <Grid container xs={12} sm={12} md={12} className={classes.ttiCardGrid}>
              <div className={classes.selectModelButton} onClick={() => navigate('/tti-train-image')}>
                <AddCircleIcon className={classes.addButtonImg} />
                <div className={classes.dragAndDropText}>{t('historyTab.tabs.ttiTraining.makeNewTraining')}</div>
              </div>
              {data
                .sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)))
                .map(({
                  id,
                  label,
                  createdAt,
                  status,
                  usage,
                  thumbnail,
                }) => {
                  return (
                    <Grid className={classes.ttiCard} item container key={id}>
                      <img
                        src={thumbnail || coverImg}
                        alt={label}
                        className={classes.ttiCardCoverImg}
                        onClick={() => {
                          if (status === 'COMPLETE') {
                            dispatch(appActions.selectEditorSource({ id, label, createdAt, usage, thumbnail }));
                            navigate('/generate-tti');
                          }
                        }}
                        style={{ cursor: status === 'COMPLETE' ? 'pointer' : '' }}
                      />
                      <div className={classes.usageTag}>{t(`historyTab.tabs.ttiTraining.${usage}`)}</div>
                      {(status === 'COMPLETE' || status === 'FAILED') && (
                        <div className={classes.modelDeleteButton} onClick={() => handleDelete(id, status, alignment)}>
                          <DeleteIcon className={classes.modelDeleteIcon} />
                        </div>
                      )}
                      <label className={classes.modelEditButton} htmlFor={`thumb-${id}`}>
                        <input
                          id={`thumb-${id}`}
                          ref={fileInputRef}
                          type="file"
                          accept=".png, .jpeg, .jpg,image/*"
                          style={{ display: 'none' }}
                          onChange={onThumbnailChange(id)}
                        />
                        <img src={editImg} alt="edit" className={classes.imgEditIcon} />
                      </label>
                      <div className={classes.ttiCardContent}>
                        <div className={classes.ttiCardContentFirstRow}>
                          <span className={classes.ttiCardLabel}>{label}</span>
                          <EditIcon
                            onClick={() => handleEdit(id, label, alignment)}
                            className={classes.ttiCardEditIcon}
                          />
                        </div>
                        <div className={classes.ttiCardTimestamp} style={{ marginBottom: '0.2rem' }}>
                          <ReactTimeAgo date={createdAt} locale="en-US" />
                          {status === 'FAILED' && <div className={classes.ttiCardStatus}>{t('historyTab.failed')}</div>}
                          {status === 'PENDING' && (
                            <div className={classes.ttiCardStatus}>{t('historyTab.inProgress')}</div>
                          )}
                        </div>
                      </div>
                    </Grid>
                  );
                })}
            </Grid>
          </InfiniteScroll>
        )}
      </div>
    </>
  );
}
export default ModelsPage;
